import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, EditableCollectionBinding, RichTextContent } from '@vibesites/runtime/react';
import { faqCategories, faqStore, toFaqItems, type Faq } from '../utils/faq';
import { useIsEditing } from '../utils/hooks';
import { useStore } from '../utils/store';

const pill =
  'group/faqcat inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-full border border-[--q-border] bg-[--q-paper] px-[18px] text-[15px] font-medium leading-5 text-[--q-ink] [transition:background-color_var(--q-motion-fast),color_var(--q-motion-fast),border-color_var(--q-motion-fast)] [&_p]:m-0 hover:border-[--q-primary-on-paper] aria-pressed:border-[--q-primary] aria-pressed:bg-[--q-primary] aria-pressed:text-[--q-on-primary] max-[767px]:shrink-0 min-[768px]:max-[1099px]:min-h-11';
const count =
  'text-[13px] font-normal leading-4 text-[--q-muted] group-aria-pressed/faqcat:text-inherit group-aria-pressed/faqcat:opacity-[.85]';

/** Topic pills with answer counts under the FAQ search; picking one filters the list below. */
export function FaqCategoryBar() {
  const editing = useIsEditing();
  const faqs = useCollectionBinding<Faq>('faqcategorybar.faqs', { collection: 'faqs' });
  const { category } = useStore(faqStore);
  const items = toFaqItems(faqs);
  const cats = faqCategories(items);

  if (cats.length < 2 && !editing) return null;

  const pick = (name: string) => faqStore.set({ category: name, query: '', focus: null });

  return (
    <section
      className="border-b border-b-[--q-border] bg-[--q-surface] px-[--gutter] pb-10 max-[767px]:pb-6 max-[767px]:pr-0"
      aria-label="FAQ topics"
    >
      <EditableCollectionBinding
        bindingPath="faqcategorybar.faqs"
        label="FAQs"
        className="flex flex-wrap justify-center gap-x-2 gap-y-2.5 max-[767px]:flex-nowrap max-[767px]:justify-start max-[767px]:overflow-x-auto max-[767px]:pr-[--gutter] max-[767px]:[scrollbar-width:none] max-[767px]:[&::-webkit-scrollbar]:hidden"
      >
        <button className={pill} type="button" aria-pressed={category === 'all'} onClick={() => pick('all')}>
          <RichTextContent path="faqcategorybar.all_label" as="span" />
          <span className={count}>{items.length}</span>
        </button>
        {cats.map((c) => (
          <button key={c.name} className={pill} type="button" aria-pressed={category === c.name} onClick={() => pick(c.name)}>
            <span>{c.name}</span>
            <span className={count}>{c.count}</span>
          </button>
        ))}
      </EditableCollectionBinding>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqCategoryBar',
  displayName: 'FAQ Topics',
  tokens: {
    'faqcategorybar.all_label': { label: 'All Pill Text', type: 'richtext', defaultValue: '<p>All</p>' },
  },
  collectionBindings: {
    'faqcategorybar.faqs': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FaqCategoryBar as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FaqCategoryBar;
